import React, { useCallback, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import QuickReading from '../../components/QuickReading/QuickReading';
import ReadingModal from '../../components/ReadingModal/ReadingModal';
import RegisterModal from '../../components/Auth/RegisterModal';
import LoginModal from '../../components/Auth/LoginModal';
import ForgotPasswordModal from '../../components/Auth/ForgotPasswordModal';
import Testimonials, { usePublicReviews } from '../../components/Testimonials/Testimonials';
import { askCards } from '../../services/readingService';
import '../Landing/Landing.css';
import './HowItWorks.css';

const HowItWorks = () => {
  const reviews = usePublicReviews();
  const [heroQuestion, setHeroQuestion] = useState('');
  const [isAskingCards, setIsAskingCards] = useState(false);
  const [askError, setAskError] = useState('');
  const [readingData, setReadingData] = useState(null);
  const [streamingInterpretation, setStreamingInterpretation] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const heroInputRef = useRef(null);
  const interpretationRef = useRef('');

  const focusAskBox = () => {
    const askSection = document.getElementById('ask-the-tarot');
    askSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    heroInputRef.current?.focus();
  };

  const handleAsk = useCallback(async (e) => {
    e.preventDefault();
    const q = heroQuestion.trim();
    if (!q || isAskingCards) return;
    setAskError('');
    setIsAskingCards(true);
    interpretationRef.current = '';
    setStreamingInterpretation('');
    await askCards(q, {
      onCards: (data) => {
        setReadingData(data);
      },
      onToken: (token) => {
        interpretationRef.current += token;
        setStreamingInterpretation(interpretationRef.current);
      },
      onDone: () => {
        setIsAskingCards(false);
      },
      onError: (msg) => {
        setIsAskingCards(false);
        setAskError(msg || 'Something went wrong. Please try again.');
      },
    });
  }, [heroQuestion, isAskingCards]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "How AI Tarot Readings Work",
        "url": "https://nowtarot.com/how-it-works/",
        "description": "How NowTarot's AI tarot readings work, why they're accurate, and answers to common questions."
      },
      {
        "@type": "HowTo",
        "name": "How to get an AI tarot chat reading",
        "step": [
          { "@type": "HowToStep", "name": "Ask your question", "text": "Type a question in chat. No credit card needed to start." },
          { "@type": "HowToStep", "name": "Read your answer", "text": "Our AI tarot reader draws cards and answers instantly." },
          { "@type": "HowToStep", "name": "Review the answer", "text": "Signed-in users can rate and comment on each LLM answer." }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How does an AI tarot reading work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Type your question, then our AI tarot reader draws cards and writes a personalized answer based on the cards, their orientation, and your question."
            }
          },
          {
            "@type": "Question",
            "name": "Is an AI tarot reading actually accurate?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. The reading engine was developed over years by someone who has practiced tarot for more than a decade, and it follows the same card meanings and spreads a human reader uses. Across thousands of readings, users consistently tell us the interpretations match what they would have drawn for themselves."
            }
          },
          {
            "@type": "Question",
            "name": "Is it really free to try?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. You can try an instant AI tarot chat reading without a credit card, and signed-in users can save, rate, and comment on answers."
            }
          },
          {
            "@type": "Question",
            "name": "Can I ask in another language?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. You can type questions in many languages. The tarot LLM understands multilingual questions and provides a clear answer."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>How AI Tarot Readings Work & Are They Accurate? | NowTarot AI</title>
        <meta name="description" content="Learn how NowTarot's AI tarot readings work, why they're surprisingly accurate, what real users say, and answers to the most common questions. Built by a 10+ year tarot reader." />
        <meta name="keywords" content="how ai tarot works, is ai tarot accurate, ai tarot reading, tarot chatbot, accurate tarot, online tarot" />
        <link rel="canonical" href="https://nowtarot.com/how-it-works/" />
        <meta property="og:title" content="How AI Tarot Readings Work & Are They Accurate? | NowTarot AI" />
        <meta property="og:description" content="How our AI tarot readings work, why they're accurate, and what real users say." />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <meta property="og:url" content="https://nowtarot.com/how-it-works/" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How AI Tarot Readings Work & Are They Accurate? | NowTarot AI" />
        <meta name="twitter:description" content="How our AI tarot readings work, why they're accurate, and what real users say." />
        <meta name="twitter:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      <div className="bg"></div>
      <div className="bg bg2"></div>
      <div className="bg bg3"></div>

      {/* Page intro */}
      <section className="hiw-intro">
        <h1>How AI Tarot Readings Work</h1>
        <p>
          Everything you want to know before your first reading — how it works,
          why it's accurate, what real people say, and the answers to the questions
          we hear most.
        </p>
        <form id="ask-the-tarot" className="hero-ask-form hiw-ask-form" onSubmit={handleAsk}>
          <input
            ref={heroInputRef}
            type="text"
            className="hero-ask-input"
            placeholder="Ask a tarot question…"
            value={heroQuestion}
            onChange={(e) => setHeroQuestion(e.target.value)}
            disabled={isAskingCards}
            maxLength={500}
          />
          <button
            type="submit"
            className="hero-ask-btn"
            disabled={!heroQuestion.trim() || isAskingCards}
          >
            {isAskingCards ? 'Drawing...' : 'Ask →'}
          </button>
        </form>
        {askError && <p className="hero-ask-error" role="alert">{askError}</p>}
        <p className="hiw-ask-note">Free instant reading — no sign-up, no credit card.</p>
      </section>

      {/* How It Works */}
      <section className="section how-it-works">
        <h2>How an AI Tarot Chat Reading Works</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="step-number">1</div>
            <h3>Ask Your Question</h3>
            <p>Type what you want guidance on. No credit card needed to start.</p>
          </div>
          <div className="feature-card">
            <div className="step-number">2</div>
            <h3>Read the Answer</h3>
            <p>Our AI tarot reader draws cards and answers in chat within seconds.</p>
          </div>
          <div className="feature-card">
            <div className="step-number">3</div>
            <h3>Rate Each Answer</h3>
            <p>When signed in, you can rate and comment on every LLM answer you receive.</p>
          </div>
        </div>
      </section>

      {/* Is it accurate? */}
      <section className="section accuracy-section">
        <h2>Is This Actually Real &amp; Accurate?</h2>
        <div className="accuracy-content">
          <p className="accuracy-lead">
            Yes — and we don't say that lightly.
          </p>
          <div className="accuracy-grid">
            <div className="accuracy-card">
              <span className="accuracy-icon">🛠️</span>
              <h3>Built by a real tarot reader</h3>
              <p>
                The reading engine wasn't thrown together. It was developed over
                years by someone who has practiced tarot for more than a decade,
                encoding the same card meanings, reversals, and spread logic a
                seasoned reader uses at the table.
              </p>
            </div>
            <div className="accuracy-card">
              <span className="accuracy-icon">📈</span>
              <h3>Proven across thousands of readings</h3>
              <p>
                We've delivered thousands of readings. Time and again, people who
                read tarot themselves tell us our interpretations match the cards
                and meanings they'd have pulled on their own — often uncannily so.
              </p>
            </div>
            <div className="accuracy-card">
              <span className="accuracy-icon">🎯</span>
              <h3>The cards answer your question</h3>
              <p>
                In tarot, the cards respond to the energy of your question — not the
                reader. There's no cold reading and no leading. Just an honest,
                consistent interpretation of the cards drawn in your moment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How the Cards Know */}
      <section className="section why-ai-section">
        <h2>How the Cards Know</h2>
        <div className="why-ai-content">
          <p className="why-ai-lead">
            In tarot, the cards respond to the energy of your question — not the reader.
          </p>
          <div className="why-ai-grid">
            <div className="why-ai-card">
              <span className="why-ai-icon">🎯</span>
              <h3>Your Question Has Power</h3>
            <p>When you type your question, you focus your intent. The cards drawn in that moment reflect what you need to hear.</p>
            </div>
            <div className="why-ai-card">
              <span className="why-ai-icon">🔮</span>
              <h3>Pure, Unbiased Guidance</h3>
              <p>No cold reading, no leading questions, no agenda. Just the cards, your question, and an honest interpretation.</p>
            </div>
            <div className="why-ai-card">
              <span className="why-ai-icon">🤫</span>
              <h3>Completely Private</h3>
              <p>Ask what you'd never ask a stranger face-to-face. No judgment, total privacy, and available whenever you need it.</p>
            </div>
          </div>
        </div>
      </section>

      <Testimonials reviews={reviews} />

      {/* Quick Card Pull */}
      <section className="section card-pull-section">
        <QuickReading onWantMoreClick={focusAskBox} />
      </section>

      {/* FAQ */}
      <section className="section faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>How does an AI tarot reading work?</h3>
            <p>
              Type your question and our AI tarot reader draws cards, then writes a personalized answer
              based on the cards, their orientation, and your question.
            </p>
          </div>
          <div className="faq-item">
            <h3>Is an AI tarot reading actually accurate?</h3>
            <p>
              Yes. The reading engine was developed over years by someone who has practiced tarot for
              more than a decade, and it follows the same card meanings and spreads a human reader uses.
              Across thousands of readings, users consistently tell us the interpretations match what
              they would have drawn for themselves.
            </p>
          </div>
          <div className="faq-item">
            <h3>Is it really free to try?</h3>
            <p>
              Yes. You can try an instant AI tarot chat reading without a credit card.
              When you sign up, you can save answers and leave a rating or comment on each one.
            </p>
          </div>
          <div className="faq-item">
            <h3>Can I ask in another language?</h3>
            <p>
              Yes. You can type questions in many languages, and the tarot LLM will understand the question
              and provide a clear answer.
            </p>
          </div>
        </div>
        <p className="free-tarot-link">
          Looking for a free card reading without signing up?{' '}
          <a href="/free-tarot/">Try our full interactive tarot reader</a>
          {' · '}
          <a href="/ask-an-ai/">Ask an AI tarot reader →</a>
        </p>
        <div className="section-cta-wrap">
          <button className="cta-button" onClick={focusAskBox}>
            Ask a Question →
          </button>
        </div>
      </section>

      <Footer />

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
          onSwitchToForgotPassword={() => {
            setShowLogin(false);
            setShowForgotPassword(true);
          }}
        />
      )}

      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          onSwitchToLogin={() => {
            setShowForgotPassword(false);
            setShowLogin(true);
          }}
        />
      )}

      {readingData && (
        <ReadingModal
          cards={readingData.cards}
          verdict={readingData.verdict}
          interpretation={streamingInterpretation}
          isStreaming={isAskingCards}
          onClose={() => {
            setReadingData(null);
            setStreamingInterpretation('');
          }}
          onSignUp={() => {
            setReadingData(null);
            setStreamingInterpretation('');
            setShowRegister(true);
          }}
          onAskAnother={() => {
            setReadingData(null);
            setStreamingInterpretation('');
            setHeroQuestion('');
            focusAskBox();
          }}
        />
      )}
    </div>
  );
};

export default HowItWorks;
